import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconSearch, IconPlus, IconAlertTriangle } from "@tabler/icons-react";
import "./patients.scss";
import { useClinicData } from "../../context/ClinicDataContext";
import NewPatientModal from "../../components/NewPatientModal/NewPatientModal";
import { BASE_PATH } from "../../components/AppLayout/AppLayout";
import { ageFrom, formatDate, formatDateShort, formatIC, formatRM, formatTime, initials } from "../../utils/format";
import { phoneMatches } from "../../utils/phone";

const FILTERS = [
  { id: "all", label: "All patients" },
  { id: "alerts", label: "Medical alerts" },
  { id: "balance", label: "Balance due" },
];

const PatientsPage = () => {
  const { patients, appointments, today, patientBalance } = useClinicData();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return patients
      .map((p) => {
        const next = appointments
          .filter((a) => a.patientId === p.id && a.date >= today && !["cancelled", "no-show", "completed"].includes(a.status))
          .sort((x, y) => (x.date + x.start).localeCompare(y.date + y.start))[0];
        return { ...p, next, balance: patientBalance(p.id) };
      })
      .filter((p) => {
        if (q) {
          const qDigits = q.replace(/\D/g, "");
          const nameHit = p.name.toLowerCase().includes(q);
          const numberHit =
            phoneMatches(p.phone, q) || (qDigits.length >= 2 && (p.ic || "").replace(/\D/g, "").includes(qDigits));
          if (!nameHit && !numberHit) return false;
        }
        if (filter === "alerts" && !p.alerts.length) return false;
        if (filter === "balance" && p.balance <= 0) return false;
        return true;
      });
  }, [patients, appointments, today, patientBalance, query, filter]);

  const countFor = (id) =>
    id === "all"
      ? patients.length
      : id === "alerts"
      ? patients.filter((p) => p.alerts.length).length
      : patients.filter((p) => patientBalance(p.id) > 0).length;

  return (
    <div className="patients-page">
      <div className="toolbar">
        <div className="toolbar__group">
          <div className="search">
            <IconSearch className="search__icon" size={18} stroke={1.75} />
            <label className="visually-hidden" htmlFor="patient-search">
              Search patients
            </label>
            <input
              id="patient-search"
              className="search__input"
              type="search"
              placeholder="Name, phone or IC"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="tabs" role="group" aria-label="Filter patients">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`tabs__item ${filter === f.id ? "tabs__item--active" : ""}`}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
                <span className="tabs__count">{countFor(f.id)}</span>
              </button>
            ))}
          </div>
        </div>
        <button className="btn btn--primary" type="button" onClick={() => setShowNew(true)}>
          <IconPlus size={18} stroke={2} />
          New patient
        </button>
      </div>

      <div className="panel panel--flush">
        <div className="data-table__wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Phone</th>
                <th>Age</th>
                <th>Last visit</th>
                <th>Next appointment</th>
                <th className="data-table__num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.id}
                  className="data-table__row--clickable"
                  onClick={() => navigate(`${BASE_PATH}/patients/${p.id}`)}
                >
                  <td>
                    <div className="patients-page__identity">
                      <span className="avatar">{initials(p.name)}</span>
                      <span>
                        <a
                          className="patients-page__name"
                          href={`${BASE_PATH}/patients/${p.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`${BASE_PATH}/patients/${p.id}`);
                          }}
                        >
                          {p.name}
                        </a>
                        {p.alerts.length > 0 && (
                          <span className="patients-page__alert">
                            <IconAlertTriangle size={13} stroke={2} />
                            {p.alerts.join(", ")}
                          </span>
                        )}
                        <span className="data-table__secondary">IC {formatIC(p.ic)}</span>
                      </span>
                    </div>
                  </td>
                  <td className="data-table__nowrap">{p.phone}</td>
                  <td>
                    {ageFrom(p.dob, today)}
                    <span className="data-table__secondary">{p.gender}</span>
                  </td>
                  <td className="data-table__nowrap">{formatDate(p.lastVisit)}</td>
                  <td>
                    {p.next ? (
                      <>
                        {formatDateShort(p.next.date)}
                        <span className="data-table__secondary">{formatTime(p.next.start)}</span>
                      </>
                    ) : (
                      <span className="patients-page__none">Not booked</span>
                    )}
                  </td>
                  <td className="data-table__num">
                    {p.balance > 0 ? <strong className="patients-page__due">{formatRM(p.balance)}</strong> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="empty-state">
              <p className="empty-state__title">No patients match "{query}"</p>
              <p>Check the spelling, or search by phone number instead.</p>
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <NewPatientModal
          onClose={() => setShowNew(false)}
          onSaved={(p) => navigate(`${BASE_PATH}/patients/${p.id}`)}
        />
      )}
    </div>
  );
};

export default PatientsPage;
